import Dexie, { Table } from 'dexie';
import { Note, Folder, NoteHistory } from '../types';

class NotepadDatabase extends Dexie {
  notes!: Table<Note>;
  folders!: Table<Folder>;
  history!: Table<NoteHistory>;

  constructor() {
    super('NotepadDB');
    
    this.version(1).stores({
      notes: 'id, title, content, type, folderId, createdAt, updatedAt, isPinned, *tags',
      folders: 'id, name, createdAt, updatedAt',
      history: 'id, noteId, timestamp, changeType'
    });
  }
}

const db = new NotepadDatabase();

export const dbService = {
  // Notes
  async getAllNotes(): Promise<Note[]> {
    return await db.notes.orderBy('updatedAt').reverse().toArray();
  },

  async getNote(id: string): Promise<Note | undefined> {
    return await db.notes.get(id);
  },

  async addNote(note: Note): Promise<void> {
    await db.notes.add(note);
    await this.addHistory(note.id, 'created', note.title, note.content);
  },

  async updateNote(id: string, updates: Partial<Note>, skipHistory: boolean = false): Promise<void> {
    const existing = await db.notes.get(id);
    if (existing) {
      await db.notes.update(id, updates);
      if (!skipHistory) {
        await this.addHistory(id, 'updated', updates.title || existing.title, updates.content || existing.content);
      }
    }
  },

  async deleteNote(id: string): Promise<void> {
    const existing = await db.notes.get(id);
    if (existing) {
      await db.notes.delete(id);
      await this.addHistory(id, 'deleted', existing.title, existing.content);
    }
  },

  async searchNotes(query: string): Promise<Note[]> {
    if (!query.trim()) return this.getAllNotes();
    
    const lowerQuery = query.toLowerCase();
    return await db.notes
      .filter(note => 
        note.title.toLowerCase().includes(lowerQuery) ||
        note.content.toLowerCase().includes(lowerQuery) ||
        note.tags.some(tag => tag.toLowerCase().includes(lowerQuery))
      )
      .toArray();
  },

  // Folders
  async getAllFolders(): Promise<Folder[]> {
    return await db.folders.orderBy('name').toArray();
  },

  async addFolder(folder: Folder): Promise<void> {
    await db.folders.add(folder);
  },

  async updateFolder(id: string, updates: Partial<Folder>): Promise<void> {
    await db.folders.update(id, updates);
  },

  async deleteFolder(id: string): Promise<void> {
    await db.folders.delete(id);
    // Move notes from deleted folder to no folder
    await db.notes.where('folderId').equals(id).modify({ folderId: undefined });
  },

  // History
  async addHistory(noteId: string, changeType: 'created' | 'updated' | 'deleted', title: string, content: string): Promise<void> {
    const historyEntry: NoteHistory = {
      id: `${noteId}-${Date.now()}`,
      noteId,
      content,
      title,
      timestamp: new Date(),
      changeType
    };
    
    await db.history.add(historyEntry);
    
    // Keep only last 50 history entries per note
    const allHistory = await db.history.where('noteId').equals(noteId).toArray();
    if (allHistory.length > 50) {
      // Sort by timestamp and keep the most recent 50
      const sortedHistory = allHistory.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      const toDelete = sortedHistory.slice(50); // Remove entries beyond the 50 most recent
      await db.history.bulkDelete(toDelete.map(h => h.id));
    }
  },

  async getNoteHistory(noteId: string): Promise<NoteHistory[]> {
    return await db.history.where('noteId').equals(noteId).reverse().sortBy('timestamp');
  },

  // Backup and restore
  async exportData() {
    const notes = await this.getAllNotes();
    const folders = await this.getAllFolders();
    const history = await db.history.toArray();
    
    return {
      notes,
      folders,
      history,
      exportDate: new Date().toISOString(),
      version: '1.0'
    };
  },

  async importData(data: any) {
    await db.transaction('rw', [db.notes, db.folders, db.history], async () => {
      if (data.notes) {
        await db.notes.clear();
        await db.notes.bulkAdd(data.notes);
      }
      if (data.folders) {
        await db.folders.clear();
        await db.folders.bulkAdd(data.folders);
      }
      if (data.history) {
        await db.history.clear();
        await db.history.bulkAdd(data.history);
      }
    });
  }
};

export default db;